import { useState, useEffect, useCallback } from 'react';
import applicationApi from '../../services/api/application.api';
import { hostApi } from '../../services/api/host.api';
import { observabilityApi } from '../../services/api/observability.api';
import alertApi from '../../services/api/alert.api';
import statusPageApi from '../../services/api/status-page.api';
import { kubernetesApi } from '../../services/api/kubernetes.api';
import uptimeMonitorService from '../../services/api/uptime-monitor.service';
import cloudScanApi from '../../services/api/cloud-scan.api';
import securityScanService from '../../services/api/security-scan.service';

export interface OnboardingStep {
  id: string;
  category: 'foundation' | 'agents' | 'scanners' | 'monitoring' | 'advanced';
  name: string;
  description: string;
  icon: string;
  completed: boolean;
  href: string;
  dependencies?: string[];
  priority: number;
}

export interface OnboardingStatus {
  steps: OnboardingStep[];
  completedCount: number;
  totalCount: number;
  percentage: number;
  currentStep: OnboardingStep | null;
  isComplete: boolean;
  loading: boolean;
  categories: {
    foundation: { completed: number; total: number };
    agents: { completed: number; total: number };
    scanners: { completed: number; total: number };
    monitoring: { completed: number; total: number };
    advanced: { completed: number; total: number };
  };
}

const STORAGE_KEY = 'onboarding_dismissed_steps';

export function useOnboardingStatus() {
  const [status, setStatus] = useState<OnboardingStatus>({
    steps: [],
    completedCount: 0,
    totalCount: 0,
    percentage: 0,
    currentStep: null,
    isComplete: false,
    loading: true,
    categories: {
      foundation: { completed: 0, total: 0 },
      agents: { completed: 0, total: 0 },
      scanners: { completed: 0, total: 0 },
      monitoring: { completed: 0, total: 0 },
      advanced: { completed: 0, total: 0 },
    },
  });

  const checkStatus = useCallback(async () => {
    try {
      setStatus(prev => ({ ...prev, loading: true }));

      // Fetch all data in parallel
      const [
        appsRes,
        hostsRes,
        dashboardRes,
        alertsRes,
        statusPagesRes,
        k8sClustersRes,
        uptimeMonitorsRes,
        cloudScanHistoryRes,
        securityDashboardRes,
      ] = await Promise.all([
        applicationApi.getAll().catch(() => ({ success: false, data: [] })),
        hostApi.getHosts(1, 0).catch(() => ({ success: false, data: [], meta: { total: 0 } })),
        observabilityApi.getDashboard('24h').catch(() => null),
        alertApi.getAll(undefined, 1, 0).catch(() => ({ success: false, data: [], meta: { total: 0 } })),
        statusPageApi.getAll().catch(() => ({ success: false, data: [] })),
        kubernetesApi.getClusters().catch(() => []),
        uptimeMonitorService.getAll({ limit: 1 }).catch(() => ({ success: false, data: [] })),
        cloudScanApi.getScanHistory({ limit: 1 }).catch(() => ({ success: false, data: { scans: [], total: 0 } })),
        securityScanService.getDashboard().catch(() => ({ success: false, data: { totalScans: 0 } })),
      ]);

      const hasApplications = appsRes.success && appsRes.data.length > 0;
      const hasHosts = hostsRes.success && (hostsRes.meta?.total || hostsRes.data?.length || 0) > 0;
      const hasTraces = dashboardRes?.overview?.tracesToday > 0;
      const hasLogs = dashboardRes?.overview?.logsToday > 0;
      const hasAlerts = alertsRes.success && (alertsRes.meta?.total || alertsRes.data?.length || 0) > 0;
      const hasStatusPages = statusPagesRes.success && statusPagesRes.data.length > 0;
      const hasRumSessions = (dashboardRes?.rum?.totalSessions || 0) > 0;
      const hasK8sClusters = Array.isArray(k8sClustersRes) && k8sClustersRes.length > 0;
      const hasUptimeMonitors = uptimeMonitorsRes.success && uptimeMonitorsRes.data.length > 0;
      const hasCloudScans = cloudScanHistoryRes.success && (cloudScanHistoryRes.data?.total || cloudScanHistoryRes.data?.scans?.length || 0) > 0;
      const hasSecurityScans = securityDashboardRes.success && (securityDashboardRes.data?.totalScans || 0) > 0;

      // Get dismissed steps from localStorage
      const dismissedSteps = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

      const steps: OnboardingStep[] = [
        // Foundation
        {
          id: 'create_application',
          category: 'foundation',
          name: 'steps.createApplication.name',
          description: 'steps.createApplication.description',
          icon: 'cube',
          completed: hasApplications,
          href: '/settings/applications',
          priority: 1,
        },

        // Agents - Data Collection
        {
          id: 'install_host_agent',
          category: 'agents',
          name: 'steps.installHostAgent.name',
          description: 'steps.installHostAgent.description',
          icon: 'server',
          completed: hasHosts,
          href: '/agents',
          dependencies: ['create_application'],
          priority: 2,
        },
        {
          id: 'install_apm_agent',
          category: 'agents',
          name: 'steps.installApmAgent.name',
          description: 'steps.installApmAgent.description',
          icon: 'chart-bar',
          completed: hasTraces,
          href: '/agents',
          dependencies: ['create_application'],
          priority: 3,
        },
        {
          id: 'install_log_agent',
          category: 'agents',
          name: 'steps.installLogAgent.name',
          description: 'steps.installLogAgent.description',
          icon: 'document-text',
          completed: hasLogs,
          href: '/agents',
          dependencies: ['create_application'],
          priority: 4,
        },
        {
          id: 'install_rum_sdk',
          category: 'agents',
          name: 'steps.installRumSdk.name',
          description: 'steps.installRumSdk.description',
          icon: 'cursor-arrow-rays',
          completed: hasRumSessions || dismissedSteps.includes('install_rum_sdk'),
          href: '/analytics/sdk-setup',
          dependencies: ['create_application'],
          priority: 5,
        },

        // Scanners - Independent
        {
          id: 'run_cloud_scan',
          category: 'scanners',
          name: 'steps.runCloudScan.name',
          description: 'steps.runCloudScan.description',
          icon: 'cloud',
          completed: hasCloudScans || dismissedSteps.includes('run_cloud_scan'),
          href: '/cloud-scan',
          priority: 6,
        },
        {
          id: 'run_security_scan',
          category: 'scanners',
          name: 'steps.runSecurityScan.name',
          description: 'steps.runSecurityScan.description',
          icon: 'shield-check',
          completed: hasSecurityScans || dismissedSteps.includes('run_security_scan'),
          href: '/security',
          priority: 7,
        },

        // Monitoring - Independent
        {
          id: 'create_uptime_monitor',
          category: 'monitoring',
          name: 'steps.createUptimeMonitor.name',
          description: 'steps.createUptimeMonitor.description',
          icon: 'clock',
          completed: hasUptimeMonitors || dismissedSteps.includes('create_uptime_monitor'),
          href: '/uptime',
          priority: 8,
        },
        {
          id: 'create_status_page',
          category: 'monitoring',
          name: 'steps.createStatusPage.name',
          description: 'steps.createStatusPage.description',
          icon: 'signal',
          completed: hasStatusPages,
          href: '/status-pages',
          priority: 9,
        },

        // Advanced - Consequences
        {
          id: 'create_alert',
          category: 'advanced',
          name: 'steps.createAlert.name',
          description: 'steps.createAlert.description',
          icon: 'bell',
          completed: hasAlerts,
          href: '/alerts',
          dependencies: ['install_host_agent', 'install_apm_agent', 'install_log_agent'],
          priority: 10,
        },
        {
          id: 'configure_kubernetes',
          category: 'advanced',
          name: 'steps.configureKubernetes.name',
          description: 'steps.configureKubernetes.description',
          icon: 'server',
          completed: hasK8sClusters || dismissedSteps.includes('configure_kubernetes'),
          href: '/infrastructure/kubernetes',
          dependencies: ['install_host_agent'],
          priority: 11,
        },
      ];

      const completedCount = steps.filter(s => s.completed).length;
      const totalCount = steps.length;
      const percentage = Math.round((completedCount / totalCount) * 100);

      // Find current step (first incomplete step with satisfied dependencies)
      const currentStep = steps.find(step => {
        if (step.completed) return false;
        if (!step.dependencies) return true;
        return step.dependencies.every(depId => steps.find(s => s.id === depId)?.completed);
      }) || null;

      // Calculate category stats
      const categories = {
        foundation: { completed: 0, total: 0 },
        agents: { completed: 0, total: 0 },
        scanners: { completed: 0, total: 0 },
        monitoring: { completed: 0, total: 0 },
        advanced: { completed: 0, total: 0 },
      };

      steps.forEach(step => {
        categories[step.category].total++;
        if (step.completed) categories[step.category].completed++;
      });

      setStatus({
        steps,
        completedCount,
        totalCount,
        percentage,
        currentStep,
        isComplete: completedCount === totalCount,
        loading: false,
        categories,
      });
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const dismissStep = useCallback((stepId: string) => {
    const dismissed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!dismissed.includes(stepId)) {
      dismissed.push(stepId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
      checkStatus();
    }
  }, [checkStatus]);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return {
    ...status,
    refresh: checkStatus,
    dismissStep,
    resetOnboarding,
  };
}

export default useOnboardingStatus;
